import * as React from 'react';
import { useCallback, ReactNode } from 'react';
import { API_PLATFORM_ENDPOINT } from '@seeneco-ui/api/modules/platform/constants';
import {
	TextField,
	PasswordTextField,
	Button,
	IconButton,
	Icon,
	Link,
	TabPicker,
	EmailTextField,
	PhoneNumberTextField,
} from '@seeneco-ui/core';
import { detectIsEmail, detectIsPhone } from '@seeneco-ui/core/utils/validation';

import { AuthResult } from '../../hooks';
import { FormTab, FormRoute } from './model';
import {
	Root,
	Content,
	ContentLayout,
	Title,
	FormLayout,
	CenterLayout,
	SubmitButtonLayout,
	InputsLayout,
	RecoveryLinkLayout,
	LogoLayout,
	Logo,
	SupportLinkLayout,
	AuthResultLayout,
	AuthResultMessage,
	RecoveryButtonLayout,
	BackButtonLayout,
	TabPickerLayout,
} from './styled';


export type AuthFormViewProps = {
	loginTitle: ReactNode;
	registrationTitle: ReactNode;
	login: string;
	password: string;
	confirmPassword: string;
	email: string;
	phoneNumber: string;
	contactName: string;
	isSubmitButtonDisabled: boolean;
	tab: FormTab;
	routeKey: FormRoute;
	recoveryRequestMessage: SuccessMessage;
	recoveryResultMessage: SuccessMessage;
	provider: string;
	businessService: string;
	systemName: string;
	isFetching: boolean;
	authResult: Record<string, AuthResult>;
	registrationError: string;
	postActionURL: string;
	tabsDataSource: Array<DataSourceItem<FormTab>>;
	onSetLogin: (value: string) => void;
	onSetPassword: (value: string) => void;
	onSetConfirmPassword: (value: string) => void;
	onSetEmail: (value: string) => void;
	onSetPhoneNumber: (value: string) => void;
	onSetContactName: (value: string) => void;
	onSetSubmitButtonDisabled: (value: boolean) => void;
	onSetRouteKey: (value: FormRoute) => void;
	onChangeTab: (value: FormTab) => void;
	onRequestRecoveryPassword: (login: string) => void;
	onRecoveryPassword: (login: string, password: string) => void;
	onRegistration: (email: string, phoneNumber: string, contactName: string) => void;
};

const AuthForm: React.SFC<AuthFormViewProps> = (props) => {
	const {
		loginTitle,
		registrationTitle,
		login,
		password,
		confirmPassword,
		email,
		phoneNumber,
		contactName,
		isSubmitButtonDisabled,
		tab,
		routeKey,
		recoveryRequestMessage,
		recoveryResultMessage,
		isFetching,
		provider,
		businessService,
		systemName,
		authResult,
		postActionURL,
		tabsDataSource,
		registrationError,
		onSetLogin,
		onSetPassword,
		onSetConfirmPassword,
		onSetEmail,
		onSetPhoneNumber,
		onSetContactName,
		onSetSubmitButtonDisabled,
		onSetRouteKey,
		onChangeTab,
		onRequestRecoveryPassword,
		onRecoveryPassword,
		onRegistration,
	} = props;

	const handleSetLogin = useCallback((_, login: string) => {
		const isButtonDisabled = detectIsActionButtonDisabled({routeKey, login, password });

		onSetLogin(login);
		onSetSubmitButtonDisabled(isButtonDisabled);
	}, [routeKey, password]);

	const handleSetPassword = useCallback((_, password: string) => {
		const isButtonDisabled = detectIsActionButtonDisabled({ routeKey, login, password });

		onSetPassword(password);
		onSetSubmitButtonDisabled(isButtonDisabled);
	}, [routeKey, login]);

	const handleSetConfirmPassword = useCallback((_, confirmPassword: string) => {
		const isButtonDisabled = detectIsActionButtonDisabled({routeKey, login, password, confirmPassword });

		onSetConfirmPassword(confirmPassword);
		onSetSubmitButtonDisabled(isButtonDisabled);
	}, [routeKey, login, password]);
	
	const handleSetEmail = useCallback((_, email: string) => {
		const isButtonDisabled = detectIsActionButtonDisabled({routeKey, email, phoneNumber, contactName });

		onSetEmail(email);
		onSetSubmitButtonDisabled(isButtonDisabled);
	}, [routeKey, phoneNumber, contactName]);

	const handleSetPhoneNumber = useCallback((_, phoneNumber: string) => {
		const isButtonDisabled = detectIsActionButtonDisabled({routeKey, email, phoneNumber, contactName });

		onSetPhoneNumber(phoneNumber);
		onSetSubmitButtonDisabled(isButtonDisabled);
	}, [routeKey, email, contactName]);

	const handleSetContactName = useCallback((_, contactName: string) => {
		const isButtonDisabled = detectIsActionButtonDisabled({routeKey, email, phoneNumber, contactName });

		onSetContactName(contactName);
		onSetSubmitButtonDisabled(isButtonDisabled);
	}, [routeKey, email, phoneNumber]);

	const handleNavigateToRecoveryZone = useCallback(() => onSetRouteKey(FormRoute.RECOVERY), []);
	const handleRequestRecoveryPassword = useCallback(() => onRequestRecoveryPassword(login), [login]);
	const handleRequestRecoveryPasswordWithKeyboard = useCallback((e: React.KeyboardEvent) => e.key === 'Enter' && handleRequestRecoveryPassword(), [login]);
	const handleRecoveryPassword = useCallback(() => onRecoveryPassword(login, password), [login, password]);
	const handleRecoveryPasswordWithKeyboard = useCallback((e: React.KeyboardEvent) => {
		e.key === 'Enter' && confirmPassword && password === confirmPassword && handleRecoveryPassword();
	}, [login, password, confirmPassword]);
	const handleNavigateToBack = useCallback(() => {
		const map = {
			[FormRoute.LOGIN]: FormRoute.LOGIN,
			[FormRoute.CHANGE_PASSWORD]: FormRoute.RECOVERY,
			[FormRoute.RECOVERY]: FormRoute.LOGIN,
		};

		onSetRouteKey(map[routeKey]);
	}, [routeKey]);

	const handleChangeTab = (_, tab: FormTab) => onChangeTab(tab);

	const handleRegistration = () => onRegistration(email, phoneNumber, contactName);

	const renderContentByRoutes = () => {
		const routeMap = {
			[FormRoute.LOGIN]: () => {
				return (
					<React.Fragment>
						<Title>{loginTitle}</Title>
						{
							Object.keys(authResult).map((key, idx) => {
								const { message } = authResult[key];

								return (
									<AuthResultLayout key={idx}>
										<AuthResultMessage appearance='alert'>
											{message}
										</AuthResultMessage>
									</AuthResultLayout>
								);
							})
						}
						<CenterLayout>
							<FormLayout>
								<form action={`${API_PLATFORM_ENDPOINT}/login`} method='post'>
									<InputsLayout>
										<input type='hidden' name='_ticketuid' value=''/>
										<input type='hidden' name='_current_pageflow_name' value=''/>
										<input type='hidden' name='_current_pageflow_node_name' value=''/>
										<input type='hidden' name='_web_service_root' value={location.origin}/>
										<input type='hidden' name='postActionURL' value={postActionURL} />
										<input type='hidden' name='requestedAction' value='login' />
										<input type='hidden' name='provider' value={provider} />
										<input type='hidden' name='business_service' value={businessService} />
										<input type='hidden' name='system_name' value={systemName} />
										<input type="hidden" name="hash" value="423b3c9d7b33fa38fabe1fb5b9b79e38e10fb632ea177e0b0dbba0c9d82bc57c" />
										<TextField
											label='Логин'
											name='user_login'
											value={login}
											autoFocus
											margin='bottom-small'
											required
											fullWidth
											onChange={handleSetLogin}
										/>
										<PasswordTextField
											label='Пароль'
											name='user_password'
											value={password}
											required
											fullWidth
											onChange={handleSetPassword}
										/>
									</InputsLayout>
									<SubmitButtonLayout>
										<Button
											type='submit'
											disabled={isSubmitButtonDisabled}>
											Войти
										</Button>
									</SubmitButtonLayout>
								</form>
							</FormLayout>
						</CenterLayout>
						<RecoveryLinkLayout>
							<Link to='' component='span' onClick={handleNavigateToRecoveryZone}>Забыли пароль?</Link>
						</RecoveryLinkLayout>
					</React.Fragment>
				);
			},
			[FormRoute.RECOVERY]: () => {
				return (
					<React.Fragment>
						<Title>Восстановление пароля</Title>
						{
							Boolean(recoveryRequestMessage) &&
							<AuthResultLayout>
								<AuthResultMessage appearance={recoveryRequestMessage.Success ? 'success' : 'alert'}>
									{recoveryRequestMessage.Message}
								</AuthResultMessage>
							</AuthResultLayout>
						}
						<CenterLayout>
							<FormLayout>
								<InputsLayout>
									<TextField
										label='Логин'
										name='user_login'
										value={login}
										autoFocus
										required
										fullWidth
										onKeyDown={handleRequestRecoveryPasswordWithKeyboard}
										onChange={handleSetLogin}
									/>
								</InputsLayout>
								<RecoveryButtonLayout>
									<Button
										disabled={isSubmitButtonDisabled || isFetching}
										isFetching={isFetching}
										onClick={handleRequestRecoveryPassword}>
										Восстановить
									</Button>
								</RecoveryButtonLayout>
							</FormLayout>
						</CenterLayout>
					</React.Fragment>
				);
			},
			[FormRoute.CHANGE_PASSWORD]: () => {
				const hasError = Boolean(confirmPassword && password !== confirmPassword);

				return (
					<React.Fragment>
						<Title>Смена пароля</Title>
						{
							Boolean(recoveryResultMessage) &&
							<AuthResultLayout>
								<AuthResultMessage appearance={recoveryResultMessage.Success ? 'success' : 'alert'}>
									{recoveryResultMessage.Message}
								</AuthResultMessage>
							</AuthResultLayout>
						}
						<CenterLayout>
							<FormLayout>
								<InputsLayout minimalMargin>
									<TextField
										label='Логин'
										name='user_login'
										value={login}
										autoFocus
										required
										fullWidth
										onChange={handleSetLogin}
									/>
									<PasswordTextField
										label='Пароль'
										value={password}
										required
										fullWidth
										onChange={handleSetPassword}
									/>
									<PasswordTextField
										label='Подтвердите новый пароль'
										value={confirmPassword}
										error={hasError}
										helperText={hasError ? 'Пароль не совпадает' : ''}
										required
										fullWidth
										onKeyDown={handleRecoveryPasswordWithKeyboard}
										onChange={handleSetConfirmPassword}
									/>
								</InputsLayout>
								<RecoveryButtonLayout>
									<Button
										disabled={isSubmitButtonDisabled || isFetching}
										isFetching={isFetching}
										onClick={handleRecoveryPassword}>
										Сменить пароль
									</Button>
								</RecoveryButtonLayout>
							</FormLayout>
						</CenterLayout>
					</React.Fragment>
				);
			},
			[FormRoute.REGISTRATION]: () => {
				return (
					<React.Fragment>
						<Title>{registrationTitle}</Title>
						{
							registrationError &&
							<AuthResultLayout>
								<AuthResultMessage appearance='alert'>
									{registrationError}
								</AuthResultMessage>
							</AuthResultLayout>
						}
						<CenterLayout>
							<FormLayout>
								<form>
									<InputsLayout>
										<EmailTextField
											label='E-mail'
											name='contactPersonEMail'
											value={email}
											margin='bottom-small'
											enableOnChangeValidation
											autoFocus
											required
											fullWidth
											onChange={handleSetEmail}
										/>
										<PhoneNumberTextField
											appearance='mobile-mask'
											label='Телефон'
											name='contactPersonPhone'
											value={phoneNumber}
											margin='bottom-small'
											required
											fullWidth
											onChange={handleSetPhoneNumber}
										/>
										<TextField
											label='Имя'
											name='contactPersonName'
											value={contactName}
											margin='bottom-small'
											placeholder='Например, Иванов Пётр Алексеевич'
											required
											fullWidth
											onChange={handleSetContactName}
										/>
									</InputsLayout>
									<SubmitButtonLayout>
										<Button
											disabled={isSubmitButtonDisabled}
											onClick={handleRegistration}>
											Зарегистрироваться
										</Button>
									</SubmitButtonLayout>
								</form>
							</FormLayout>
						</CenterLayout>
					</React.Fragment>
				);
			}
		};

		return routeMap[routeKey]();
	};

	const backButtonWhiteList = [FormRoute.RECOVERY, FormRoute.CHANGE_PASSWORD];
	const needRenderBackButton = backButtonWhiteList.includes(routeKey);
	const tabsWhiteList = [FormRoute.LOGIN, FormRoute.REGISTRATION];
	const needRenderTabs = tabsWhiteList.includes(routeKey);

	return (
		<Root>
			<Content>
				<LogoLayout>
					<Logo>
						<u>Seeneco</u>
					</Logo>
				</LogoLayout>
				{
					needRenderTabs &&
					<TabPickerLayout>
						<TabPicker
							value={tab}
							dataSource={tabsDataSource}
							negativeOffset={40}
							fullWidth
							onChange={handleChangeTab}
						/>
					</TabPickerLayout>
				}
				<ContentLayout>
					{
						needRenderBackButton &&
						<BackButtonLayout>
							<IconButton title='Назад' onClick={handleNavigateToBack}>
								<Icon type='arrow-back' color='muted' width={20} height={20} />
							</IconButton>
						</BackButtonLayout>
					}
					{renderContentByRoutes()}
				</ContentLayout>
			</Content>
			<SupportLinkLayout>
				<Link to='mailto:support@seeneco.ru' color='muted'>Написать в службу поддержки</Link>
			</SupportLinkLayout>
		</Root>
	);
}

export type DataSourceItem<T = any> = { value: T; name: string };

type DetectIsActionButtonDisabledOptions = {
	routeKey: string;
	login?: string;
	password?: string;
	confirmPassword?: string;
	email?: string;
	phoneNumber?: string;
	contactName?: string;
};

const detectIsActionButtonDisabled = (options: DetectIsActionButtonDisabledOptions): boolean => {
	const {
		routeKey,
		login,
		password,
		confirmPassword = '',
		email,
		phoneNumber,
		contactName,
	} = options;
	const map = {
		[FormRoute.LOGIN]: () => !Boolean(login && password),
		[FormRoute.RECOVERY]: () => !Boolean(login),
		[FormRoute.CHANGE_PASSWORD]: () => !Boolean(login && confirmPassword && confirmPassword === password),
		[FormRoute.REGISTRATION]: () => !Boolean(detectIsEmail(email) && detectIsPhone(phoneNumber) && contactName.length > 2),
	};

	return map[routeKey]();
};

export default AuthForm;